/**
 * Public session API boundary.
 * Defines the exact interface the UI (or external clients) will talk to.
 */

import { DEGAPSession } from "../sessions/orchestrator";
import type {
  Item,
  CriteriaSet,
  Lens,
  ToolConfig,
} from "../harmonizer/types";
import type { PrioritySnapshot, PriorityState } from "./priorityTruth";
import { applySnapshot } from "./priorityTruth";
import { runArrangeToolWithAudit, emitPrioritySnapshot } from "../arrange/audit";
import type { ArrangeAuditSink } from "../arrange/audit";
import {
  buildSnapshotExecutiveView,
  buildSnapshotTraceView,
  getItemExecutiveViews,
  getItemTraceViews,
  type ExecutivePriorityView,
  type TracePriorityView,
} from "./explainability";
import {
  renderExecutiveSummary,
  renderItemRationales,
  renderSnapshotDelta,
} from "./decisionRenderer";
import { computeDrift, type DriftReport } from "./drift";
import type { ToolConfig } from "../harmonizer/types";
import type { AllowedSessionMode, ResultResolutionStrategy } from "../types/prioritization/workflow";
import type { SessionRunMode } from "../types/prioritization/session";

// The in-memory session's preset record. These types lived in
// src/lib/sessionPresetResolver.ts, a browser walk of the session defaults that
// nothing called; it was removed when the server's walk took its place (S-40,
// get_session_defaults, migration 1039).
export type SessionPresetSource = "cycle_workflow" | "workspace_default_workflow" | "canonical";
export type SessionCriteriaSource =
  | "selected_criteria"
  | "applied_template_attributes"
  | "workflow_default_attributes"
  | "fallback_default";

export interface SessionPresetContext {
  workspaceId: string | null;
  cycleId: string | null;
  cycleName?: string | null;
  workflowId: string | null;
  workflowName?: string | null;
  source: SessionPresetSource;
}

export interface SessionPresetSnapshot {
  runMode: SessionRunMode;
  /** @deprecated Read runMode. */
  timingMode: "live" | "async";
  /** @deprecated Read runMode. */
  entryMode: "facilitator" | "participants";
  defaultFacilitatorId: string | null;
  initialCriteriaIds: string[];
  criteriaSource: SessionCriteriaSource;
  recommendedToolId: string | null;
  toolDefaultsByTool: Record<string, ToolConfig>;
  resultResolution: ResultResolutionStrategy;
  catchballEnabled: boolean;
  allowedSessionModes: AllowedSessionMode[];
}
import { SESSION_CONTEXT_MODES } from "../types/prioritization/workflow";

/**
 * Session identifier type.
 */
export type SessionId = string;

export interface SessionOverrides {
  criteriaIds?: string[];
  selectedTool?: string | null;
  toolConfig?: ToolConfig;
}

export interface SessionMetadata {
  context: SessionPresetContext;
  presetSnapshot: SessionPresetSnapshot;
  overrides: SessionOverrides;
  createdAt: number;
  updatedAt: number;
}

export interface SessionCreateOptions {
  initialItems?: Item[];
  initialCriteriaSet?: CriteriaSet | null;
  metadata?: Partial<SessionMetadata>;
}

/**
 * In-memory audit sink that collects events for replay.
 */
class InMemoryAuditSink implements ArrangeAuditSink {
  events: Array<import("../arrange/audit").ArrangeAuditEvent> = [];

  async emit(event: import("../arrange/audit").ArrangeAuditEvent): Promise<void> {
    this.events.push(event);
  }

  getEvents(): Array<import("../arrange/audit").ArrangeAuditEvent> {
    return [...this.events];
  }

  clear(): void {
    this.events = [];
  }
}

/**
 * Session data stored in the API.
 */
type SessionData = {
  session: DEGAPSession;
  state: PriorityState;
  auditSink: InMemoryAuditSink;
  userId: string;
  metadata: SessionMetadata;
};

/**
 * Public session API interface.
 * 
 * Provides a clean, stable interface for:
 * - Creating and managing sessions
 * - Running tools
 * - Retrieving priority state
 * - Adding items
 */
export interface SessionApi {
  /**
   * Creates a new session with initial items.
   * 
   * @param initialItems - Initial items for the session
   * @returns Session ID
   */
  createSession(initialItemsOrOptions?: Item[] | SessionCreateOptions): SessionId;

  /**
   * Gets a session by ID.
   * 
   * @param sessionId - Session identifier
   * @returns DEGAP session, or undefined if not found
   */
  getSession(sessionId: SessionId): DEGAPSession | undefined;

  /**
   * Gets session metadata such as inherited presets and local overrides.
   */
  getSessionMetadata(sessionId: SessionId): SessionMetadata | undefined;

  /**
   * Updates session metadata in memory.
   */
  updateSessionMetadata(
    sessionId: SessionId,
    updates: Partial<SessionMetadata> | ((current: SessionMetadata) => SessionMetadata)
  ): void;

  /**
   * Adds an item to a session.
   * 
   * @param sessionId - Session identifier
   * @param item - Item to add
   */
  addItem(sessionId: SessionId, item: Item): Promise<void>;

  /**
   * Runs a tool and creates a priority snapshot.
   * 
   * @param sessionId - Session identifier
   * @param toolName - Name of the tool to run
   * @param toolConfig - Tool-specific configuration
   * @param lens - Optional lens for filtering/sorting
   * @returns Priority snapshot created from the tool run
   */
  runTool(
    sessionId: SessionId,
    toolName: string,
    toolConfig: ToolConfig,
    lens: Lens | null
  ): Promise<PrioritySnapshot>;

  /**
   * Gets the current priority state for a session.
   * 
   * @param sessionId - Session identifier
   * @returns Priority state, or undefined if session not found
   */
  getPriorityState(sessionId: SessionId): PriorityState | undefined;

  /**
   * Gets all audit events for a session.
   * 
   * @param sessionId - Session identifier
   * @returns Array of audit events, or undefined if session not found
   */
  getAuditEvents(sessionId: SessionId): Array<import("../arrange/audit").ArrangeAuditEvent> | undefined;

  /**
   * Gets an executive priority view for a snapshot.
   * 
   * @param sessionId - Session identifier
   * @param snapshotId - Snapshot identifier
   * @returns Executive priority view, or undefined if not found
   */
  getExecutiveView(
    sessionId: SessionId,
    snapshotId: string
  ): ExecutivePriorityView | undefined;

  /**
   * Gets a trace priority view for a snapshot.
   * 
   * @param sessionId - Session identifier
   * @param snapshotId - Snapshot identifier
   * @returns Trace priority view, or undefined if not found
   */
  getTraceView(
    sessionId: SessionId,
    snapshotId: string
  ): TracePriorityView | undefined;

  /**
   * Gets rendered explanations for a snapshot.
   * 
   * @param sessionId - Session identifier
   * @param snapshotId - Snapshot identifier
   * @returns Rendered explanations with executive summary and item rationales
   */
  getRenderedExplanations(
    sessionId: SessionId,
    snapshotId: string
  ): {
    executiveSummary: string;
    itemRationales: Record<string, string>;
    snapshotId: string;
    toolName: string;
    createdAt: number;
  } | undefined;

  /**
   * Gets drift report for a map.
   * 
   * @param sessionId - Session identifier (used as mapId)
   * @returns Drift report, or null if no published snapshot found
   */
  getDriftReportForMap(sessionId: SessionId): DriftReport | null;
}

/**
 * Options for creating a session API.
 */
export interface SessionApiOptions {
  defaultUserId?: string;
  itemRepository?: import("./repositories").ItemRepository;
  criteriaRepository?: import("./repositories").CriteriaRepository;
  snapshotRepository?: import("./repositories").SnapshotRepository;
}

/**
 * Creates an in-memory session API implementation.
 * 
 * Uses a Map to store sessions in memory. No persistence.
 * This is a pure, in-process API suitable for testing and single-instance use.
 * 
 * @param defaultUserId - Default user ID for sessions (defaults to "system")
 * @returns Session API instance
 */
export function createInMemorySessionApi(
  defaultUserId: string = "system"
): SessionApi {
  return createSessionApi({ defaultUserId });
}

/**
 * Creates a session API with optional repositories for persistence.
 * 
 * @param options - Configuration options
 * @returns Session API instance
 */
export function createSessionApi(options: SessionApiOptions = {}): SessionApi {
  const {
    defaultUserId = "system",
    itemRepository,
    criteriaRepository,
    snapshotRepository,
  } = options;
  const sessions = new Map<SessionId, SessionData>();
  let nextSessionId = 1;

  // Helper to save items if repository is available
  const saveItemsIfRepo = async (sessionId: SessionId, items: Item[]) => {
    if (itemRepository) {
      try {
        await itemRepository.saveItems(sessionId, items);
      } catch (error) {
        console.warn("Failed to persist items:", error);
      }
    }
  };

  // Helper to save snapshot if repository is available
  const saveSnapshotIfRepo = async (snapshot: PrioritySnapshot) => {
    if (snapshotRepository) {
      try {
        await snapshotRepository.saveSnapshot(snapshot);
      } catch (error) {
        console.warn("Failed to persist snapshot:", error);
      }
    }
  };

  const createDefaultMetadata = (): SessionMetadata => ({
    context: {
      workspaceId: null,
      cycleId: null,
      workflowId: null,
      source: 'canonical',
    },
    presetSnapshot: {
      timingMode: 'live',
      entryMode: 'facilitator',
      defaultFacilitatorId: null,
      initialCriteriaIds: [],
      criteriaSource: 'fallback_default',
      recommendedToolId: null,
      toolDefaultsByTool: {},
      resultResolution: 'latest_published',
      catchballEnabled: true,
      allowedSessionModes: [...SESSION_CONTEXT_MODES],
    },
    overrides: {},
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });

  return {
    createSession(initialItemsOrOptions: Item[] | SessionCreateOptions = []): SessionId {
      const sessionId = `session-${nextSessionId++}`;
      const createOptions = Array.isArray(initialItemsOrOptions)
        ? { initialItems: initialItemsOrOptions }
        : initialItemsOrOptions;
      const initialItems = createOptions.initialItems ?? [];
      const session = new DEGAPSession(initialItems);
      if (createOptions.initialCriteriaSet) {
        session.arrange.criteriaSet = createOptions.initialCriteriaSet;
      }
      const auditSink = new InMemoryAuditSink();
      const baseMetadata = createDefaultMetadata();
      const metadata: SessionMetadata = {
        ...baseMetadata,
        ...createOptions.metadata,
        context: {
          ...baseMetadata.context,
          ...(createOptions.metadata?.context ?? {}),
        },
        presetSnapshot: {
          ...baseMetadata.presetSnapshot,
          ...(createOptions.metadata?.presetSnapshot ?? {}),
        },
        overrides: {
          ...baseMetadata.overrides,
          ...(createOptions.metadata?.overrides ?? {}),
        },
        createdAt: createOptions.metadata?.createdAt ?? baseMetadata.createdAt,
        updatedAt: Date.now(),
      };

      sessions.set(sessionId, {
        session,
        state: {
          latestSnapshot: null,
          history: [],
        },
        auditSink,
        userId: defaultUserId,
        metadata,
      });

      return sessionId;
    },

    getSession(sessionId: SessionId): DEGAPSession | undefined {
      return sessions.get(sessionId)?.session;
    },

    getSessionMetadata(sessionId: SessionId): SessionMetadata | undefined {
      const data = sessions.get(sessionId);
      if (!data) return undefined;
      return {
        ...data.metadata,
        context: { ...data.metadata.context },
        presetSnapshot: {
          ...data.metadata.presetSnapshot,
          toolDefaultsByTool: { ...data.metadata.presetSnapshot.toolDefaultsByTool },
        },
        overrides: {
          ...data.metadata.overrides,
          toolConfig: data.metadata.overrides.toolConfig ? { ...data.metadata.overrides.toolConfig } : undefined,
        },
      };
    },

    updateSessionMetadata(sessionId: SessionId, updates): void {
      const data = sessions.get(sessionId);
      if (!data) {
        throw new Error(`Session ${sessionId} not found`);
      }

      const nextMetadata = typeof updates === 'function'
        ? updates(data.metadata)
        : {
            ...data.metadata,
            ...updates,
            context: {
              ...data.metadata.context,
              ...(updates.context ?? {}),
            },
            presetSnapshot: {
              ...data.metadata.presetSnapshot,
              ...(updates.presetSnapshot ?? {}),
              toolDefaultsByTool: {
                ...data.metadata.presetSnapshot.toolDefaultsByTool,
                ...(updates.presetSnapshot?.toolDefaultsByTool ?? {}),
              },
            },
            overrides: {
              ...data.metadata.overrides,
              ...(updates.overrides ?? {}),
            },
          };

      data.metadata = {
        ...nextMetadata,
        updatedAt: Date.now(),
      };
    },

    async addItem(sessionId: SessionId, item: Item): Promise<void> {
      const data = sessions.get(sessionId);
      if (!data) {
        throw new Error(`Session ${sessionId} not found`);
      }

      data.session.addItem(item);
      
      // Persist if repository available
      if (itemRepository) {
        const items = data.session.gather.items;
        await saveItemsIfRepo(sessionId, items);
      }
    },

    async runTool(
      sessionId: SessionId,
      toolName: string,
      toolConfig: ToolConfig,
      lens: Lens | null
    ): Promise<PrioritySnapshot> {
      const data = sessions.get(sessionId);
      if (!data) {
        throw new Error(`Session ${sessionId} not found`);
      }

      const { session, state, auditSink, userId } = data;

      // Get current items and criteria set from session
      const items = session.gather.items;
      const criteriaSet = session.arrange.criteriaSet;

      // Run tool with audit
      const scoreSet = await runArrangeToolWithAudit(
        auditSink,
        sessionId,
        userId,
        toolName,
        items,
        criteriaSet,
        toolConfig,
        lens
      );

      // Emit priority snapshot
      await emitPrioritySnapshot(
        auditSink,
        sessionId,
        userId,
        toolName,
        scoreSet,
        criteriaSet,
        lens
      );

      // Create priority snapshot
      const snapshot: PrioritySnapshot = {
        snapshotId: `${sessionId}-${Date.now()}-${toolName}`,
        sessionId,
        toolName,
        createdAt: Date.now(),
        scoreSet,
      };

      // Apply snapshot to state
      const newState = applySnapshot(state, snapshot);
      data.state = newState;

      // Persist snapshot if repository available
      await saveSnapshotIfRepo(snapshot);

      return snapshot;
    },

    getPriorityState(sessionId: SessionId): PriorityState | undefined {
      return sessions.get(sessionId)?.state;
    },

    getAuditEvents(sessionId: SessionId): Array<import("../arrange/audit").ArrangeAuditEvent> | undefined {
      return sessions.get(sessionId)?.auditSink.getEvents();
    },

    getExecutiveView(
      sessionId: SessionId,
      snapshotId: string
    ): ExecutivePriorityView | undefined {
      const data = sessions.get(sessionId);
      if (!data) {
        return undefined;
      }

      // Find the snapshot
      const snapshot =
        data.state.latestSnapshot?.snapshotId === snapshotId
          ? data.state.latestSnapshot
          : data.state.history.find((s) => s.snapshotId === snapshotId);

      if (!snapshot) {
        return undefined;
      }

      // Get items and events
      const items = data.session.gather.items;
      const events = data.auditSink.getEvents();

      return buildSnapshotExecutiveView(snapshot, items, events);
    },

    getTraceView(
      sessionId: SessionId,
      snapshotId: string
    ): TracePriorityView | undefined {
      const data = sessions.get(sessionId);
      if (!data) {
        return undefined;
      }

      // Find the snapshot
      const snapshot =
        data.state.latestSnapshot?.snapshotId === snapshotId
          ? data.state.latestSnapshot
          : data.state.history.find((s) => s.snapshotId === snapshotId);

      if (!snapshot) {
        return undefined;
      }

      // Get items and events
      const items = data.session.gather.items;
      const events = data.auditSink.getEvents();

      return buildSnapshotTraceView(snapshot, items, events);
    },

    getRenderedExplanations(
      sessionId: SessionId,
      snapshotId: string
    ): {
      executiveSummary: string;
      itemRationales: Record<string, string>;
      snapshotId: string;
      toolName: string;
      createdAt: number;
    } | undefined {
      const data = sessions.get(sessionId);
      if (!data) {
        return undefined;
      }

      // Find the snapshot
      const snapshot =
        data.state.latestSnapshot?.snapshotId === snapshotId
          ? data.state.latestSnapshot
          : data.state.history.find((s) => s.snapshotId === snapshotId);

      if (!snapshot) {
        return undefined;
      }

      // Get items and events
      const items = data.session.gather.items;
      const events = data.auditSink.getEvents();

      // Build views
      const executiveView = buildSnapshotExecutiveView(snapshot, items, events);
      const traceView = buildSnapshotTraceView(snapshot, items, events);

      // Render explanations
      const executiveSummary = renderExecutiveSummary(
        executiveView,
        snapshot,
        items.length
      );

      // Get item trace views and render rationales
      const itemTraceViews = getItemTraceViews(snapshot, items, events);
      const itemRationales: Record<string, string> = {};

      for (const [itemId, trace] of itemTraceViews.entries()) {
        const rationales = renderItemRationales(trace, items);
        Object.assign(itemRationales, rationales);
      }

      return {
        executiveSummary,
        itemRationales,
        snapshotId: snapshot.snapshotId,
        toolName: snapshot.toolName,
        createdAt: snapshot.createdAt,
      };
    },

    getDriftReportForMap(sessionId: SessionId): DriftReport | null {
      const data = sessions.get(sessionId);
      if (!data) {
        return null;
      }

      // Find the published snapshot (for now, use latestSnapshot as published)
      // In a full implementation, we'd track which snapshot is published
      const publishedSnapshot = data.state.latestSnapshot;
      if (!publishedSnapshot) {
        return null;
      }

      // Get current items
      const items = data.session.gather.items;

      // Compute drift (using a simple cut line for now)
      // In a full implementation, cut line would come from the snapshot or map config
      const cutLine = Math.ceil(publishedSnapshot.scoreSet.scores.length / 2);
      
      return computeDrift(publishedSnapshot, items, cutLine);
    },
  };
}

/**
 * Creates a session API with localStorage persistence.
 * 
 * @param defaultUserId - Default user ID for sessions (defaults to "system")
 * @returns Session API instance with localStorage-backed repositories
 */
/**
 * Creates a session API with localStorage persistence.
 * 
 * @param defaultUserId - Default user ID for sessions (defaults to "system")
 * @returns Session API instance with localStorage-backed repositories
 */
export async function createSessionApiWithPersistence(
  defaultUserId: string = "system"
): Promise<SessionApi> {
  // Dynamic import for ES modules
  const {
    createLocalStorageItemRepository,
    createLocalStorageCriteriaRepository,
    createLocalStorageSnapshotRepository,
  } = await import("./localStorageRepositories.js");

  const itemRepo = createLocalStorageItemRepository();
  const criteriaRepo = createLocalStorageCriteriaRepository();
  const snapshotRepo = createLocalStorageSnapshotRepository();

  return createSessionApi({
    defaultUserId,
    itemRepository: itemRepo,
    criteriaRepository: criteriaRepo,
    snapshotRepository: snapshotRepo,
  });
}

/**
 * Synchronous version that uses require for compatibility.
 * Use this in environments where dynamic import is not available.
 */
export function createSessionApiWithPersistenceSync(
  defaultUserId: string = "system"
): SessionApi {
  // @ts-ignore - require for compatibility
  const {
    createLocalStorageItemRepository,
    createLocalStorageCriteriaRepository,
    createLocalStorageSnapshotRepository,
  } = require("./localStorageRepositories");

  const itemRepo = createLocalStorageItemRepository();
  const criteriaRepo = createLocalStorageCriteriaRepository();
  const snapshotRepo = createLocalStorageSnapshotRepository();

  return createSessionApi({
    defaultUserId,
    itemRepository: itemRepo,
    criteriaRepository: criteriaRepo,
    snapshotRepository: snapshotRepo,
  });
}

